import {
    AppNotificationType,
    AutoBackmergeStrategy,
    AutoCherryPickStrategy,
    AutomationType,
    BumpVersionAutomationStrategy,
    ProvisioningProfileType,
    PullRequestType,
    ReleaseNotesDataSource,
    SettingField,
    SettingFieldType,
    SettingHelpTextBehavior,
    SettingRadioDirection,
    SettingOption,
    RunwayPRAssignee,
} from '@/types';

// Temporary hand copy of lib/autoconfig/settings_definition.go, since demo apps can't reach the backend; see runway-wiki/Demo-Apps.md
const demoUserGroupOptions: SettingOption[] = [
    { value: 'demo-engineering', label: 'Engineering' },
    { value: 'demo-qa', label: 'QA' },
    { value: 'demo-product', label: 'Product' },
];

const buildReleaseNotesSourceOptions = (defaultLabel: string): SettingOption[] => [
    { value: ReleaseNotesDataSource.DefaultReleaseNotes, label: defaultLabel },
    { value: ReleaseNotesDataSource.PreviousRelease, label: 'Pull in from previous release' },
    { value: ReleaseNotesDataSource.None, label: "Don't apply automatically" },
];

const buildWaitUntilKickedOffField = (noun: string): SettingField => ({
    key: 'shouldWaitUntilNextReleaseIsKickedOff',
    type: SettingFieldType.Switch,
    label: 'Sync when the current release is kicked off',
    helpText: `If this option is turned off, the sync will happen as soon as the previous version has been released. In practice, this may mean that the ${noun} may reference the next release's pilot while the previous release is still rolling out.`,
    helpTextBehavior: SettingHelpTextBehavior.Tooltip,
});

const bypassPullRequestsKey = 'shouldCommitAndPushDirectlyToTargetBranch';

const bypassPullRequestsField: SettingField = {
    key: bypassPullRequestsKey,
    type: SettingFieldType.Switch,
    label: 'Bypass pull requests',
    helpText:
        "If your repository has branch protection enabled for the target branch AND those branch protections include requiring pull requests to merge or restricting push access to your target branch, you'll need to update your branch protection rules to allow Runway to bypass requirements.",
    helpTextBehavior: SettingHelpTextBehavior.Tooltip,
};

const buildAssignPrToField = (
    canAssignFixOwner: boolean,
    isDisabledWhenBypassingPullRequests: boolean,
): SettingField => ({
    key: 'assignPrTo',
    type: SettingFieldType.Select,
    label: 'Pull request assignee',
    helpText: `Choose who Runway should assign to the pull requests it opens. Has no effect when the chosen user has no linked version control account.${
        isDisabledWhenBypassingPullRequests ?
            ' Unavailable while pull requests are bypassed, since Runway commits directly to the target branch instead of opening one.'
        :   ''
    }`,
    helpTextBehavior: SettingHelpTextBehavior.Tooltip,
    defaultValue: RunwayPRAssignee.None,
    options: [
        { value: RunwayPRAssignee.None, label: 'Leave PR unassigned' },
        { value: RunwayPRAssignee.ReleasePilot, label: 'Assign PR to release pilot' },
        ...(canAssignFixOwner ?
            [{ value: RunwayPRAssignee.FixOwner, label: 'Assign PR to the fix owner or code author' }]
        :   []),
    ],
    disabledWhenEquals: isDisabledWhenBypassingPullRequests ? { [bypassPullRequestsKey]: true } : undefined,
});

const isCherryPicking = { strategy: AutoBackmergeStrategy.ContinuousCherryPick };

// Keyed by type rather than per automation, so platform-dependent fields use their iOS version
export const demoAutomationSettingFieldsByType: Partial<Record<AutomationType, SettingField[]>> = {
    [AutomationType.AutoSyncAppleDevices]: [
        {
            key: 'autoSyncAppleDevicesPossibleUserGroups',
            type: SettingFieldType.MultiSelect,
            label: 'Register devices for app users in Runway user groups',
            helpText:
                'Only devices associated with users that have one of the selected user groups will be synced. Leave blank to sync devices for all app users',
            options: demoUserGroupOptions,
        },
        {
            key: 'autoSyncAppleDevicesShouldIncludeTestDevices',
            type: SettingFieldType.Switch,
            label: 'Include test devices',
            helpText: 'Runway will sync devices that have been added to the app as test devices',
            defaultValue: true,
        },
        {
            key: 'autoSyncAppleDevicesShouldDisableDevices',
            type: SettingFieldType.Switch,
            label: 'Disable devices for users that are removed from apps in Runway',
            defaultValue: true,
        },
    ],
    [AutomationType.AutoSyncAppleProvisioningProfilesDevices]: [
        {
            key: 'autoSyncAppleProvisioningProfilesDevicesProfileTypes',
            type: SettingFieldType.MultiSelect,
            label: 'Profile types',
            helpText: 'Runway will sync the associated devices of selected profile types only',
            required: true,
            options: [
                { value: ProvisioningProfileType.IosDevelopment, label: 'iOS Development' },
                { value: ProvisioningProfileType.IosAdHoc, label: 'iOS Ad Hoc' },
            ],
        },
        {
            key: 'autoSyncAppleProvisioningProfilesDevicesUserGroups',
            type: SettingFieldType.MultiSelect,
            label: 'User groups',
            helpText:
                'Runway will only sync the devices of app users with at least one of the selected user groups. Leave blank to sync devices for all app users.',
            options: demoUserGroupOptions,
        },
        {
            key: 'autoSyncAppleProvisioningProfilesDevicesShouldRemoveDevices',
            type: SettingFieldType.Switch,
            label: 'Remove devices for users that are removed from apps in Runway',
            defaultValue: true,
        },
    ],
    [AutomationType.AutoSyncSlackPilotUserGroup]: [
        {
            key: 'userGroupHandle',
            type: SettingFieldType.Input,
            label: 'Release pilot handle',
            helpText:
                'Runway will automatically manage the user group for this handle, ensuring the current release pilot is its only member.',
            required: true,
            pattern: '^@?[a-zA-Z0-9_-]+$',
            patternMessage: 'use only letters (a–z), numbers (0–9), hyphens (-), and underscores (_)',
        },
        buildWaitUntilKickedOffField('handle'),
    ],
    [AutomationType.AutoSyncTeamsPilotTag]: [
        {
            key: 'tagName',
            type: SettingFieldType.Input,
            label: 'Release pilot tag',
            helpText:
                'Runway will automatically manage the Teams tag with this name, ensuring the current release pilot is its only member.',
            required: true,
        },
        buildWaitUntilKickedOffField('tag'),
    ],
    [AutomationType.AutoTagReleases]: [
        {
            key: 'fallbackToHeadIfNeeded',
            type: SettingFieldType.Switch,
            label: 'Allow fallback to tagging HEAD of release branch if Runway is unable to match released build to specific commit',
        },
    ],
    [AutomationType.AutoGenerateCIBuildDiffSummary]: [
        {
            key: 'showSummaryOnCIStep',
            type: SettingFieldType.Switch,
            label: 'Show summary on the CI step',
            defaultValue: true,
        },
        {
            key: 'notificationIdsToInclude',
            type: SettingFieldType.MultiSelect,
            label: 'Include summary in build notifications',
            options: [
                { value: AppNotificationType.BuildAvail, label: 'New build available' },
                { value: AppNotificationType.BuildAvailArtifacts, label: 'New build available with artifacts' },
            ],
        },
    ],
    [AutomationType.AutoPullSubmissionFromReview]: [
        {
            key: 'pullOnReleaseSkipped',
            type: SettingFieldType.Switch,
            label: 'Pull a submission from review if the release is skipped',
        },
        {
            key: 'pullOnFixOrFixRequestAccepted',
            type: SettingFieldType.Switch,
            label: 'Pull a submission from review if a fix is added / fix request is approved',
        },
    ],
    [AutomationType.AutoApplyMetadata]: [
        {
            key: 'releaseNotesSource',
            type: SettingFieldType.Select,
            label: "'What's new' text",
            helpText: "You can set default 'What's new' text from App settings > Flightpath > Metadata",
            defaultValue: ReleaseNotesDataSource.DefaultReleaseNotes,
            options: buildReleaseNotesSourceOptions("Use default 'What's new' text"),
        },
        {
            key: 'promoTextSource',
            type: SettingFieldType.Select,
            label: 'Promotional text',
            helpText: 'You can set default promotional text from App settings > Flightpath > Metadata',
            defaultValue: ReleaseNotesDataSource.None,
            options: buildReleaseNotesSourceOptions('Use default promotional text'),
        },
    ],
    [AutomationType.AutoDistributeReleaseSummary]: [
        {
            key: 'emailAddresses',
            type: SettingFieldType.Tags,
            label: 'Additional recipients',
            helpText:
                'Enter up to five additional recipients. Note that by default all app users will receive release summaries and can opt out independently.',
            placeholder: 'e.g. product@team.com',
        },
    ],
    [AutomationType.AutoAddMissingFAs]: [
        {
            key: 'useWorkspaceLabels',
            type: SettingFieldType.Switch,
            label: 'Create labels at workspace level instead of team level',
        },
    ],
    [AutomationType.AutoPromoteBuildsToTestTrack]: [
        {
            key: 'testingTrack',
            type: SettingFieldType.Input,
            label: 'Testing track',
            helpText: "Choose from 'alpha', 'beta', 'internal', or another custom testing track",
            required: true,
        },
    ],
    [AutomationType.AutoCreateRunwayReleases]: [
        {
            key: 'destinations',
            type: SettingFieldType.DestinationOptions,
            label: 'Destinations',
            isLabelHidden: true,
        },
        {
            key: 'delayUntilBackmergeMerged',
            type: SettingFieldType.Switch,
            label: "Wait until current release's backmerge is merged",
            helpText:
                "Do not create the new release until the current release's backmerge PR is merged. This option requires the 'Backmerge changes from the release branch' automation to be enabled.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
    ],
    [AutomationType.AutoSyncMetadataTranslations]: [
        {
            key: 'autoSyncMetadataTranslationsBeforeSubmissionEnabled',
            type: SettingFieldType.Switch,
            label: 'Export translated metadata ahead of release submission',
            helpText:
                'An export will be triggered for any metadata fields in an Export needed state. Some metadata fields may still have a status of Waiting for translation when the export is triggered.',
            helpTextBehavior: SettingHelpTextBehavior.Below,
        },
        {
            key: 'daysBeforeSubmissionForExport',
            type: SettingFieldType.Integer,
            label: 'When to export',
            required: true,
            defaultValue: 1,
            min: 1,
            max: 30,
            unitAddon: 'days before submission',
            visibleWhenEquals: { autoSyncMetadataTranslationsBeforeSubmissionEnabled: true },
        },
    ],
    [AutomationType.AutoBumpVersion]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Bump version strategy',
            helpText: 'Choose when Runway should bump the version for each release.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            required: true,
            options: [
                {
                    value: BumpVersionAutomationStrategy.PreKickoffToCurrentVersionOnWorkingBranch,
                    label: 'Pre-kickoff, to current release version, on working branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostKickoffToCurrentVersionOnReleaseBranch,
                    label: 'Post-kickoff, to current release version, on release branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostKickoffToNextVersionOnWorkingBranch,
                    label: 'Post-kickoff, to next version, on working branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostSubmitToNextVersionOnWorkingBranch,
                    label: 'Post-submission, to next version, on working branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostReleaseToNextVersionOnWorkingBranch,
                    label: 'Post-release, to next version, on working branch',
                },
            ],
        },
        {
            key: 'skipInvalidFiles',
            type: SettingFieldType.Switch,
            label: 'Skip missing files',
            helpText:
                "If enabled, the bump version operation will skip any configured file that can't be fetched from the target branch, rather than failing the entire bump.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        bypassPullRequestsField,
        buildAssignPrToField(false, true),
    ],
    [AutomationType.AutoKickoffRelease]: [
        {
            key: 'allowKickoffWhenPreviousNotComplete',
            type: SettingFieldType.Radio,
            label: 'Allow kickoff when previous release not complete?',
            options: [
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
            ],
            radioDirection: SettingRadioDirection.Horizontal,
        },
        bypassPullRequestsField,
    ],
    [AutomationType.AutoCherryPickLocalizableStrings]: [bypassPullRequestsField, buildAssignPrToField(false, true)],
    [AutomationType.AutoBackmergeReleaseBranches]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Backmerge strategy',
            defaultValue: AutoBackmergeStrategy.OnReleaseCompletion,
            options: [
                {
                    value: AutoBackmergeStrategy.OnReleaseCompletion,
                    label: 'Backmerge changes from the release branch all at once, at the end of each release cycle',
                },
                {
                    value: AutoBackmergeStrategy.Continuous,
                    label: 'Backmerge changes from the release branch continuously as they are merged',
                },
                {
                    value: AutoBackmergeStrategy.ContinuousCherryPick,
                    label: 'Cherry-pick changes from the release branch continuously as they are merged',
                },
            ],
        },
        {
            key: 'runwayPRTypesToSkip',
            type: SettingFieldType.MultiSelect,
            label: 'Runway PR types to skip',
            placeholder: 'Select Runway PR types to skip',
            options: [
                { value: PullRequestType.BumpVersion, label: 'Bump version' },
                { value: PullRequestType.PromoteCode, label: 'Promote code' },
                { value: PullRequestType.BackmergeReleasePRCommit, label: 'Continuously backmerge release branch' },
                { value: PullRequestType.CherryPickWorkingBranch, label: 'Cherry-pick from working branch' },
                { value: PullRequestType.ManualConflictResolution, label: 'Manual cherry-pick conflict resolution' },
                {
                    value: PullRequestType.ManualConflictResolutionOpenedByUser,
                    label: 'Manual cherry-pick conflict resolution (opened by user)',
                },
                { value: PullRequestType.TranslationsUpdate, label: 'Translations update' },
                { value: PullRequestType.HotfixCherryPick, label: 'Hotfix cherry-pick' },
                { value: PullRequestType.SyncMetadataToVCS, label: 'Release metadata update' },
            ],
            visibleWhenEquals: isCherryPicking,
        },
        {
            key: 'shouldBackmergeOnlyMergeCommits',
            type: SettingFieldType.Switch,
            label: 'Only backmerge merge commits',
            visibleWhenEquals: isCherryPicking,
        },
        {
            key: 'onlyBackmergeRunwayPRs',
            type: SettingFieldType.Switch,
            label: 'Only backmerge PRs created by Runway',
            visibleWhenEquals: isCherryPicking,
        },
        buildAssignPrToField(true, false),
    ],
    [AutomationType.AutoCherryPickFromWorkingBranch]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Pull over fixes...',
            helpText:
                "One at a time: pull requests containing fixes against the release branch must be merged or closed before the next fix can be pulled in. As soon as they're detected: multiple pull requests containing fixes against the release branch can be open at the same time. Grouped: a single pull request containing all fixes will be open against the release branch, which you must merge manually once all fixes are in.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            required: true,
            defaultValue: AutoCherryPickStrategy.SingleInSequence,
            options: [
                {
                    value: AutoCherryPickStrategy.SingleInSequence,
                    label: '...one at a time in the order in which they were merged',
                },
                {
                    value: AutoCherryPickStrategy.SingleInParallel,
                    label: "...in the order in which they were merged, as soon as they're detected",
                },
                {
                    value: AutoCherryPickStrategy.Cumulative,
                    label: "...by grouping all fixes into a single PR, in the order in which they were merged, as soon as they're detected",
                },
            ],
        },
        {
            key: 'requiresFixApprovalToCherryPick',
            type: SettingFieldType.Switch,
            label: 'Require fix approval before performing cherry-pick',
            helpText:
                "Fixes must be approved in Runway before they can be automatically cherry-picked. 'Fix approvals' must be enabled from App settings > General > Fixes.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        {
            key: 'shouldNotifyPRAuthors',
            type: SettingFieldType.Switch,
            label: 'Notify the original author when a PR has been cherry-picked',
        },
        {
            key: 'skipPostingPRCommentsOnCherryPickSuccess',
            type: SettingFieldType.Switch,
            label: 'Skip posting PR comments on cherry-pick success',
            helpText:
                'By default Runway comments on the original pull-request once its commits have been cherry-picked. Check this to stop posting that comment.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        {
            key: 'skipPostingPRCommentsOnCherryPickMergeConflicts',
            type: SettingFieldType.Switch,
            label: 'Skip posting PR comments on cherry-pick merge conflicts',
            helpText:
                'By default Runway comments on the original pull-request when a cherry-pick hits a merge conflict. Check this to stop posting that comment.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        buildAssignPrToField(true, false),
    ],
};
